import { Controller } from "@hotwired/stimulus"

// Keeps a Scenario's line recordings speaking one at a time: starting a line pauses
// whichever other line is playing. With `advance` (the learner reader) a line that
// finishes hands over to the next line that has a recording, so the conversation
// plays through from wherever the learner started it. Lines without a recording are
// skipped — the learner reads them at their own pace.
//
// Scoped to the Scenario block on purpose: the shared media-player stays unaware of
// its neighbours everywhere else.
export default class extends Controller {
  static values = { advance: Boolean }

  connect() {
    this.boundPlay = (event) => this.handlePlay(event)
    this.boundEnded = (event) => this.handleEnded(event)
    // Media events do not bubble, so they are only visible in the capture phase.
    this.element.addEventListener("play", this.boundPlay, true)
    this.element.addEventListener("ended", this.boundEnded, true)
  }

  disconnect() {
    this.element.removeEventListener("play", this.boundPlay, true)
    this.element.removeEventListener("ended", this.boundEnded, true)
  }

  handlePlay(event) {
    const media = event.target
    if (!(media instanceof HTMLAudioElement)) return

    this.recordings.forEach((recording) => {
      if (recording !== media && !recording.paused) recording.pause()
    })
  }

  handleEnded(event) {
    const media = event.target
    if (!this.advanceValue || !(media instanceof HTMLAudioElement)) return

    const recordings = this.recordings
    const next = recordings[recordings.indexOf(media) + 1]
    if (!next) return

    try { next.currentTime = 0 } catch (_error) { /* not seekable yet */ }
    // A refused or broken clip ends the run rather than skipping ahead silently; the
    // learner can start any later line by hand.
    next.play().catch(() => {})
  }

  // Read live: the builder adds and removes recordings without re-rendering the block.
  get recordings() {
    return Array.from(this.element.querySelectorAll(".app-media-player audio"))
  }
}
